import { ApiInput } from "libs/ApiTypes";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import * as ops from "libs/operations";
import { withActionContext, write } from "./write";
import { toast } from "~/environment/toast-service";
import * as actions from "libs/actions";

export type CreateUserParams = ApiInput<"createUser">;

/**
 * Note that this is not an "optimistic" operation. We wait for it
 * to be confirmed by the server. Also, we don't support creating
 * users while offline.
 */
export const createUser = async function (environment: ClientEnvironment, params: CreateUserParams) {
  using disposable = environment.isLoading.add();

  const { status, body } = await environment.api.createUser(params);

  if (status !== 200) return;
  if (!body.recordMap) return;

  environment.db.writeRecordMap(body.recordMap);
};

export type UpdateUserProps = {
  userId: string;
  profile?: Omit<ops.user.UpdateUserNameProps, "userId">;
  contactInfo?: Omit<ops.user.UpdateUserContactInfoProps, "userId">;
};

export const updateUser = withActionContext(async (context, environment, props: UpdateUserProps) => {
  const [[originalUserProfile], [originalUserContactInfo]] = await Promise.all([
    context.environment.loader.getRecord("user_profile", props.userId),
    context.environment.loader.getRecord("user_contact_info", props.userId),
  ]);

  return {
    forward: async () => {
      using disposable = environment.isLoading.add();

      const action = await actions.user.updateUser.action(props, context);

      await write(environment, { action });
    },
    backward: async () => {
      using disposable = environment.isLoading.add();

      if (!originalUserProfile || !originalUserContactInfo) {
        throw new actions.InvalidActionStateError(`[updateUser] No original user profile or contact info found.`, {
          errorData: { userId: props.userId },
        });
      }

      const action = await actions.user.updateUser.action(
        {
          profile: {
            firstName: originalUserProfile.first_name,
            lastName: originalUserProfile.last_name,
            middleName: originalUserProfile.middle_name,
            name: originalUserProfile.name,
          },
          contactInfo: {
            phoneNumber: originalUserContactInfo.phone_number,
          },
        },
        context,
      );

      await write(environment, {
        action,
        onOptimisticWrite: () => {
          toast("vanilla", { subject: "Undoing user profile update." });
        },
      });
    },
  };
});
